import { isMembershipPurchaseLine as isMembershipLine } from '@notifi/shared-types/membership-line';
import { soldOnComplimentaryOption } from './complimentary-option';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';

export interface MembershipPlanSummary {
  short: string;
  recurring: string;
  discount?: string;
  coveredServices: Array<{ name: string; quantityPerPeriod: number; period: string }>;
  contractTerm?: string;
  /** Customer-facing selling bullets, in saved order. */
  valuePoints: string[];
  /** Sold on a free, office-only billing option (a $0 base; extras may bill). */
  complimentary?: boolean;
}

interface MembershipPurchaseSummarySnapshot {
  billingIntervalUnit?: unknown;
  billingIntervalValue?: unknown;
  contractLengthMonths?: unknown;
  planSnapshot?: unknown;
  priceAmount?: unknown;
}

/**
 * How often an authored visit lands, said the way the schedule actually runs.
 *
 * Never annualised to a rounded integer: a visit every third spring is one
 * visit every three years, and rounding that to a yearly rate reported ZERO
 * included services — a plan promising nothing, on the screen where it is sold.
 */
function describeCadence(service: {
  recurrenceType?: string | null;
  recurrenceInterval?: number | null;
  recurrenceMonths?: number[] | null;
}): { quantityPerPeriod: number; period: string } {
  const interval = Math.max(1, Number(service.recurrenceInterval) || 1);
  const type = (service.recurrenceType ?? 'MONTHLY').toUpperCase();

  if (type === 'SEASONAL') {
    // The listed months, every `interval` YEARS.
    const months = service.recurrenceMonths?.length ?? 0;
    return interval === 1
      ? { quantityPerPeriod: months, period: 'per year' }
      : { quantityPerPeriod: months, period: `every ${interval} years` };
  }

  const perYear =
    type === 'DAILY' ? 365 / interval : type === 'WEEKLY' ? 52 / interval : 12 / interval;
  // Only a cadence that divides the year evenly can be stated as a yearly
  // count. Every 8 months is not "2 per year" — it is three visits every two
  // years, and rounding it promises a visit the plan never delivers.
  if (Number.isInteger(perYear)) {
    return { quantityPerPeriod: perYear, period: 'per year' };
  }
  // Slower than once a year, said in the unit the schedule is written in.
  // Converting to years turns a visit every 18 months into "every 2 years",
  // which is a promise the plan does not make.
  const unit = type === 'DAILY' ? 'days' : type === 'WEEKLY' ? 'weeks' : 'months';
  return { quantityPerPeriod: 1, period: `every ${interval} ${unit}` };
}

const PERIOD_LABEL: Record<string, string> = {
  MONTHS: 'mo',
  YEARS: 'yr',
};

const ENTITLEMENT_PERIOD_LABEL: Record<string, string> = {
  BILLING_CYCLE: 'per billing cycle',
  ANNUAL: 'per year',
  CONTRACT_TERM: 'per contract',
  LIFETIME: 'lifetime',
};

const ITEM_TYPE_LABEL: Record<string, string> = {
  SERVICE: 'services',
  MATERIAL: 'materials',
  EQUIPMENT: 'equipment',
  FEE: 'fees',
};

function formatList(values: string[]): string {
  if (values.length <= 1) return values[0] ?? '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(', ')}, and ${values.at(-1)}`;
}

function safeNumber(value: unknown): number {
  const numberValue = Number(value ?? 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
}

function formatMoney(amount: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(amount);
}

function recurringIntervalLabel(value: number, unit: string): string {
  const periodUnit = PERIOD_LABEL[unit] ?? unit.toLowerCase();
  if (value <= 1) return `/ ${periodUnit}`;
  return `every ${value} ${periodUnit}`;
}

export function formatMembershipPlanSummary(
  plan: MembershipPlan,
  { complimentaryOption = false }: { complimentaryOption?: boolean } = {},
): MembershipPlanSummary {
  const currency = plan.priceCurrency || 'USD';
  const billingIntervalValue = Math.max(1, safeNumber(plan.billingIntervalValue));
  const periodUnit =
    PERIOD_LABEL[plan.billingIntervalUnit] ?? plan.billingIntervalUnit.toLowerCase();
  const price = safeNumber(plan.priceAmount);
  const pricedRecurring = `${formatMoney(price, currency)} ${recurringIntervalLabel(
    billingIntervalValue,
    plan.billingIntervalUnit,
  )}`;
  // A free option's base is $0; what is left to bill is the extra units.
  const freeMonths = safeNumber(plan.contractLengthMonths);
  const recurring = !complimentaryOption
    ? pricedRecurring
    : price > 0
      ? `${pricedRecurring} for extra units (free base)`
      : freeMonths > 0
        ? `Free for ${freeMonths} months`
        : 'Free';
  // NOT-257: a plan with custom rate tiers stores discountPercent = 0, so a
  // single "X% off" can't represent it — surface "Custom member rates" instead.
  const discountPercent = safeNumber(plan.discountPercent);
  const discountScopes = (plan.eligibleLineItemTypes ?? [])
    .map((type) => ITEM_TYPE_LABEL[type.toUpperCase()])
    .filter((value): value is string => Boolean(value));
  const discount =
    (plan.discountTiers?.length ?? 0) > 0
      ? 'Custom member rates'
      : discountPercent > 0
        ? `${discountPercent}% off ${formatList(
            // Older frozen snapshots did not carry the scope. Their discount
            // was service-only, so retain that meaning instead of broadening
            // the sold promise when the document is reopened.
            discountScopes.length > 0 ? discountScopes : ['services'],
          )}`
        : undefined;
  // What the plan includes, read off the visits it actually authors. The
  // `coveredServices` array is the model those replaced: nothing writes it any
  // more, so a plan authored today listed nothing here — the picker's "what
  // this includes" panel was empty for every plan on the shelf. Legacy plans
  // that still carry it keep being described by it.
  const authoredVisits = (plan.recurringServices ?? []).map((service) => {
    // A legacy capped visit promises a total, not a rate. Describing it per
    // year would tell the customer it renews forever.
    const cap = Number(service.totalVisits);
    return service.durationType === 'FIXED' && Number.isFinite(cap) && cap > 0
      ? {
          name: service.label?.trim() || 'Included visit',
          quantityPerPeriod: cap,
          period: 'in total',
        }
      : {
          name: service.label?.trim() || 'Included visit',
          ...describeCadence(service),
        };
  });
  const coveredServices =
    authoredVisits.length > 0
      ? authoredVisits
      : (plan.coveredServices ?? []).map((service) => ({
          name: service.name,
          quantityPerPeriod: safeNumber(service.quantityPerPeriod),
          period:
            ENTITLEMENT_PERIOD_LABEL[service.entitlementPeriodType] ??
            service.entitlementPeriodType.toLowerCase(),
        }));
  const contractLengthMonths = safeNumber(plan.contractLengthMonths);
  const contractTerm =
    contractLengthMonths > 0 ? `${contractLengthMonths}-month contract` : undefined;

  const shortParts = [recurring];
  if (coveredServices.length > 0) {
    const totalQuantity = coveredServices.reduce(
      (sum, service) => sum + service.quantityPerPeriod,
      0,
    );
    // Only rows that count the same way can be added up. A plan mixing a capped
    // promise with a recurring one has no single rate — summing them told the
    // customer the capped visits renew forever.
    const periods = new Set(coveredServices.map((service) => service.period));
    if (periods.size > 1) {
      shortParts.push(
        `${coveredServices.length} included service${coveredServices.length === 1 ? '' : 's'}`,
      );
    } else {
      const period = [...periods][0] ?? 'per year';
      const coveredUnit =
        authoredVisits.length === 0
          ? periodUnit
          : period === 'in total'
            ? 'plan'
            : period === 'per year'
              ? 'yr'
              : period;
      shortParts.push(`${totalQuantity} service${totalQuantity === 1 ? '' : 's'} / ${coveredUnit}`);
    }
  } else if (discount) {
    shortParts.push(discount);
  }

  return {
    short: shortParts.join(' · '),
    recurring,
    discount,
    coveredServices,
    contractTerm,
    valuePoints: plan.display?.valuePoints ?? [],
    ...(complimentaryOption ? { complimentary: true } : {}),
  };
}

/**
 * Rebuilds the small editor-facing summary from the immutable purchase
 * snapshot returned by estimates and proposals.
 *
 * `membershipPlanSummary` is a UI convenience and is not persisted. The
 * authoritative purchase snapshot is, so a reopened document must derive the
 * cadence and discount copy from that frozen record instead of dropping it.
 */
export function membershipPlanSummaryFromPurchaseSnapshot(
  value: unknown,
): MembershipPlanSummary | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const purchase = value as MembershipPurchaseSummarySnapshot;
  if (!purchase.planSnapshot || typeof purchase.planSnapshot !== 'object') return undefined;

  const planSnapshot = purchase.planSnapshot as Record<string, unknown>;
  const priceAmount = Number(purchase.priceAmount ?? planSnapshot.priceAmount);
  const billingIntervalValue = Number(
    purchase.billingIntervalValue ?? planSnapshot.billingIntervalValue,
  );
  const billingIntervalUnit = String(
    purchase.billingIntervalUnit ?? planSnapshot.billingIntervalUnit ?? '',
  );
  if (
    !Number.isFinite(priceAmount) ||
    !Number.isFinite(billingIntervalValue) ||
    !billingIntervalUnit
  ) {
    return undefined;
  }

  return formatMembershipPlanSummary(
    {
      ...planSnapshot,
      priceAmount,
      billingIntervalValue,
      billingIntervalUnit,
      contractLengthMonths:
        purchase.contractLengthMonths == null
          ? (planSnapshot.contractLengthMonths as number | null | undefined)
          : Number(purchase.contractLengthMonths),
    } as unknown as MembershipPlan,
    { complimentaryOption: soldOnComplimentaryOption(planSnapshot) },
  );
}

export function membershipBillingSummaryFromLineItems(
  lineItems: Array<{
    type?: unknown;
    taxCategory?: unknown;
    isSelected?: boolean;
    membershipPurchaseSnapshot?: unknown;
  }>,
): string | undefined {
  // THE shared answer — a membership says so by TYPE or by `taxCategory`, and
  // the same function decides it in operations. A row this filter cannot see is
  // a plan summary the customer's document does not print.
  const membershipRows = lineItems.filter(
    (lineItem) =>
      isMembershipLine({
        type: lineItem.type == null ? null : String(lineItem.type),
        taxCategory: lineItem.taxCategory == null ? null : String(lineItem.taxCategory),
      }) && lineItem.isSelected !== false,
  );
  if (membershipRows.length !== 1) return undefined;
  return membershipPlanSummaryFromPurchaseSnapshot(membershipRows[0]?.membershipPurchaseSnapshot)
    ?.recurring;
}
